import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtPKR } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  MessageCircle,
  CheckCircle2,
  XCircle,
  Calendar,
  CalendarClock,
  AlertTriangle,
  Send,
  Save,
  Zap,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import PageTip from "../components/PageTip";

const BUCKETS = {
  before_due: { label: "Due soon", tint: "amber", icon: CalendarClock },
  due_today: { label: "Due today", tint: "sky", icon: Calendar },
  overdue: { label: "Overdue", tint: "red", icon: AlertTriangle },
};

const BUCKET_TONE = {
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  sky: "bg-sky-50 text-sky-700 border-sky-200",
  red: "bg-red-50 text-red-700 border-red-200",
};

function BucketCard({ type, count, testid }) {
  const cfg = BUCKETS[type];
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-md p-5">
      <div className={`w-10 h-10 rounded-md border flex items-center justify-center ${BUCKET_TONE[cfg.tint]}`}>
        <cfg.icon className="w-5 h-5" />
      </div>
      <div className="mt-4 text-xs text-slate-500 uppercase tracking-wide font-semibold">{cfg.label}</div>
      <div className="mt-1 font-display text-2xl font-bold tabular">{count ?? 0}</div>
      <div className="text-xs text-slate-500 mt-1">customer(s) will receive a reminder</div>
    </div>
  );
}

export default function WhatsApp() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [preview, setPreview] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [savedTemplates, setSavedTemplates] = useState(null);
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [running, setRunning] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const loadAll = async () => {
    const [p, t, l] = await Promise.all([
      api.get("/whatsapp/preview"),
      api.get("/whatsapp/templates"),
      api.get("/whatsapp/logs"),
    ]);
    setPreview(p.data);
    setTemplates(t.data);
    setSavedTemplates(t.data);
    setLogs(l.data);
  };

  useEffect(() => {
    loadAll();
  }, []);

  const runAuto = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/whatsapp/run");
      setLastResult(data);
      setResultOpen(true);
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to run reminders");
    } finally {
      setRunning(false);
    }
  };

  const saveTemplates = async () => {
    try {
      await api.put("/whatsapp/templates", templates);
      setSavedTemplates(templates);
      toast.success("Templates saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    }
  };

  const templatesDirty =
    templates && savedTemplates &&
    (templates.before_due !== savedTemplates.before_due ||
      templates.due_today !== savedTemplates.due_today ||
      templates.overdue !== savedTemplates.overdue);

  const filteredLogs = logs.filter((l) => (filter === "all" ? true : l.status === filter));

  return (
    <div className="space-y-6" data-testid="whatsapp-page">
      <PageTip
        id="whatsapp"
        icon={MessageCircle}
        text="Send payment reminders and agreements directly through WhatsApp. Templates fully customizable hain — zero paid API."
      />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2">
            <MessageCircle className="w-7 h-7 text-emerald-600" />
            WhatsApp Reminders
          </h1>
          <p className="text-slate-500 mt-1">
            Automatic reminder system — detects due-soon, due-today and overdue loans and sends templated messages.
          </p>
        </div>
        <Button
          onClick={runAuto}
          disabled={running || !preview}
          className="bg-emerald-600 hover:bg-emerald-700 text-white h-11 px-5"
          data-testid="run-auto-reminder-btn"
        >
          <Zap className="w-4 h-4 mr-1.5" />
          {running ? "Running..." : `Run Auto Reminder ${preview ? `(${preview.total})` : ""}`}
        </Button>
      </div>

      <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 flex items-start gap-2">
        <Info className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          Click-to-WhatsApp uses your <strong>own WhatsApp Business number</strong> — no API, no monthly fee. Connect it in <a href="/settings" className="underline font-semibold">Settings</a>, then use the prepared messages below to send via your phone.
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <BucketCard type="before_due" count={preview?.counts?.before_due} testid="card-before-due" />
        <BucketCard type="due_today" count={preview?.counts?.due_today} testid="card-due-today" />
        <BucketCard type="overdue" count={preview?.counts?.overdue} testid="card-overdue" />
      </div>

      {/* Templates editor */}
      <div className="bg-white border border-slate-200 rounded-md">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="font-display font-semibold text-lg">Message templates</div>
            <div className="text-xs text-slate-500">
              Variables: <code className="bg-slate-100 px-1 rounded">{"{{name}}"}</code>{" "}
              <code className="bg-slate-100 px-1 rounded">{"{{amount}}"}</code>{" "}
              <code className="bg-slate-100 px-1 rounded">{"{{date}}"}</code>{" "}
              <code className="bg-slate-100 px-1 rounded">{"{{shop}}"}</code>
            </div>
          </div>
          {isOwner && (
            <Button
              onClick={saveTemplates}
              disabled={!templatesDirty}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="save-templates-btn"
            >
              <Save className="w-4 h-4 mr-1" /> Save templates
            </Button>
          )}
        </div>
        <div className="p-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
          {templates &&
            Object.keys(BUCKETS).map((k) => {
              const cfg = BUCKETS[k];
              return (
                <div key={k} className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide font-semibold flex items-center gap-1.5">
                    <cfg.icon className={`w-3.5 h-3.5 text-${cfg.tint === "red" ? "red" : cfg.tint === "amber" ? "amber" : "sky"}-600`} />
                    {cfg.label}
                  </Label>
                  <Textarea
                    rows={5}
                    value={templates[k] || ""}
                    onChange={(e) => setTemplates({ ...templates, [k]: e.target.value })}
                    disabled={!isOwner}
                    data-testid={`template-${k}`}
                    className="text-sm"
                  />
                </div>
              );
            })}
        </div>
        {!isOwner && (
          <div className="px-5 pb-5 text-xs text-slate-500">Only shop owners can edit templates.</div>
        )}
      </div>

      {/* Reminder log */}
      <div className="bg-white border border-slate-200 rounded-md">
        <div className="p-5 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-display font-semibold text-lg">Reminder log</div>
            <div className="text-xs text-slate-500">All reminder messages (most recent first).</div>
          </div>
          <div className="flex gap-1.5">
            {["all", "sent", "failed"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold border capitalize ${
                  filter === f ? "bg-slate-900 text-white border-slate-900" : "border-slate-200"
                }`}
                data-testid={`filter-${f}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">When</th>
                <th className="text-left px-4 py-3 font-semibold">Customer</th>
                <th className="text-left px-4 py-3 font-semibold">Bucket</th>
                <th className="text-left px-4 py-3 font-semibold">Message</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-slate-500" data-testid="wa-empty">
                    No reminders yet. Click "Run Auto Reminder" to send the first batch.
                  </td>
                </tr>
              )}
              {filteredLogs.map((l) => {
                const cfg = BUCKETS[l.bucket] || BUCKETS.overdue;
                return (
                  <tr key={l.id} className="border-t border-slate-100 align-top" data-testid={`wa-log-${l.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap text-xs tabular text-slate-600">
                      {new Date(l.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <Link to={`/customers/${l.customer_id}`} className="font-medium hover:text-emerald-700">
                        {l.customer_name || "—"}
                      </Link>
                      <div className="text-xs text-slate-500 tabular">{l.mobile || "(no mobile)"}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold border ${BUCKET_TONE[cfg.tint]}`}>
                        <cfg.icon className="w-3 h-3" />
                        {cfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700 max-w-[420px]">
                      <div className="text-sm whitespace-pre-wrap leading-snug">{l.message}</div>
                    </td>
                    <td className="px-4 py-3">
                      {l.status === "sent" ? (
                        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`status-sent-${l.id}`}>
                          <CheckCircle2 className="w-3 h-3" /> Sent
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold bg-red-50 text-red-700 border border-red-200" data-testid={`status-failed-${l.id}`}>
                          <XCircle className="w-3 h-3" /> Failed
                        </span>
                      )}
                      {l.error && <div className="text-[10px] text-slate-500 mt-0.5">{l.error}</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Run result popup */}
      <AlertDialog open={resultOpen} onOpenChange={setResultOpen}>
        <AlertDialogContent data-testid="run-result-dialog">
          <AlertDialogHeader>
            <div className="w-12 h-12 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center mx-auto mb-2">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            </div>
            <AlertDialogTitle className="text-center">Messages sent successfully</AlertDialogTitle>
            <AlertDialogDescription className="text-center space-y-3">
              <span className="block text-slate-700">
                {lastResult?.sent ?? 0} message(s) sent
                {lastResult?.failed ? ` · ${lastResult.failed} failed` : ""}
                {lastResult?.skipped ? ` · ${lastResult.skipped} skipped (already sent today)` : ""}
                .
              </span>
              {lastResult && (
                <span className="grid grid-cols-3 gap-2 text-xs">
                  <span className="rounded-md border border-amber-200 bg-amber-50 text-amber-800 py-2 font-semibold tabular">
                    Due soon · {lastResult.counts?.before_due ?? 0}
                  </span>
                  <span className="rounded-md border border-sky-200 bg-sky-50 text-sky-800 py-2 font-semibold tabular">
                    Due today · {lastResult.counts?.due_today ?? 0}
                  </span>
                  <span className="rounded-md border border-red-200 bg-red-50 text-red-800 py-2 font-semibold tabular">
                    Overdue · {lastResult.counts?.overdue ?? 0}
                  </span>
                </span>
              )}
              <span className="block text-xs text-slate-500">
                Simulated — no real WhatsApp API was called.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction className="bg-emerald-600 hover:bg-emerald-700 w-full" data-testid="run-result-ok-btn">
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
